import { useEffect, useRef, useState } from 'react';

const HANDSHAKE_FALLBACK_MS = 3000;

type SyncMutationHandshakeInput = {
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
};

/**
 * POST /sync can return before GET /sync/status reports `hasActiveJobs` (see useSyncAutomationTemplates).
 * Returns true while a sync mutation is in flight or until status catches up (or a short fallback timeout).
 *
 * React Query keeps `isSuccess` true after the mutation settles, so when polling later reports
 * `hasActiveJobs: false` we must not re-run the POST/polling handshake timeout — that would keep
 * the UI locked for an extra 3 seconds after sync already finished.
 */
export function useSyncStatusHandshakeLock(
  jobSyncActive: boolean,
  syncMutation: SyncMutationHandshakeInput,
): boolean {
  const [locked, setLocked] = useState(false);
  /** True once this mutation's sync has shown up in GET /sync/status (hasActiveJobs). */
  const hasObservedActiveSyncJobRef = useRef(false);
  const { isPending, isError, isSuccess } = syncMutation;

  useEffect(() => {
    if (isPending) {
      hasObservedActiveSyncJobRef.current = false;
      setLocked(true);
      return;
    }
    if (isError) {
      setLocked(false);
      return;
    }
    if (jobSyncActive) {
      hasObservedActiveSyncJobRef.current = true;
      setLocked(false);
      return;
    }
    if (hasObservedActiveSyncJobRef.current) {
      setLocked(false);
      return;
    }
    if (isSuccess) {
      const t = window.setTimeout(() => setLocked(false), HANDSHAKE_FALLBACK_MS);
      return () => clearTimeout(t);
    }
  }, [jobSyncActive, isPending, isError, isSuccess]);

  return locked;
}
